import React, { useState, useCallback, useEffect } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { GenericModal } from '..';
import { WelcomePopupModalProps } from './types';
import CompleteProfileModal from './complete-profile-modal.component';
import BusinessProfileModal from './business-profile-modal.component';
import './welcome-popup-modal.style.scss';
import WelcomeCuate1 from '../../../../../public/assets/svg/Welcome-cuate-1.svg';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useRedux';
import { updateUserOnboardingService } from '../../../features/userOnboarding/userOnboarding.slice';
import { user } from '../../images/jpg/healthInsuranceIcons';

type ModalStep = 'welcome' | 'profile' | 'closed';

const WelcomePopupModal: React.FC<WelcomePopupModalProps> = ({
  show,
  userType,
  handleStartTour,
  close,
  setShowWelcomeModal,
  initialStep,
}) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>('welcome');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // When opened from the dashboard card with a target step, skip the welcome
  // splash and go straight to the profile modal (which deep-links further).
  useEffect(() => {
    if (show) {
      setCurrentStep(initialStep ? 'profile' : 'welcome');
    }
  }, [show, initialStep]);

  // Step 1: Mark onboarding as complete and close everything
  const completeOnboarding = useCallback(async () => {
    try {
      await dispatch(updateUserOnboardingService({ actionType: 'IsCloseSteps', value: true }));
    } catch (error) {
      console.error('Failed to update onboarding service:', error);
    }
  }, [dispatch]);

  // Step 2: Close all modals completely — hide IMMEDIATELY (so the X closes the
  // modal right away from any step, with no flash of an earlier step) and persist
  // "onboarding closed" in the background.
  const closeAllModals = useCallback(() => {
    setCurrentStep('closed');
    setShowWelcomeModal(false);
    close();
    void completeOnboarding();
  }, [completeOnboarding, close, setShowWelcomeModal]);

  // Step 3: Handle welcome modal close button
  const handleWelcomeClose = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  // Step 4: Handle welcome modal next button - go to profile step
  // Fully close the welcome modal first, then open the profile modal on the
  // next tick. Swapping two react-bootstrap modals within the same render can
  // leave the previous modal/backdrop lingering, so we sequence the handoff.
  const handleWelcomeNext = useCallback(() => {
    setCurrentStep('closed');
    setTimeout(() => setCurrentStep('profile'), 0);
  }, []);

  const handleProfileClose = useCallback(() => {
    // Deep-linked from the dashboard card → there's no welcome splash to return
    // to, so closing the profile closes the flow cleanly.
    if (initialStep) {
      closeAllModals();
    } else {
      setCurrentStep('welcome');
    }
  }, [initialStep, closeAllModals]);

  const handleProfileNext = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  const handleStartTourClick = useCallback(() => {
    closeAllModals();
    if (handleStartTour) {
      handleStartTour();
    }
  }, [closeAllModals, handleStartTour]);

  const isWelcomeStep = currentStep === 'welcome';
  const isProfileStep = currentStep === 'profile';
  return (
    <>
      {/* Step 1: Welcome Modal */}
      <div className='welcome-popup-container-main'>
        <GenericModal
          show={show && isWelcomeStep && !initialStep}
          onClose={handleWelcomeClose}
          size="xl"
          showFooter={false}
          showCloseButton={false}
          closeOnBackdrop={true}
          className="welcome-popup-modal"
        >
          <div className="welcome-popup-content">
            <div className="welcome-popup-header">
              <button
                type="button"
                className="welcome-popup-close-btn"
                onClick={handleWelcomeClose}
                aria-label="Close"
              >
                <AiOutlineClose size={32} />
              </button>
            </div>

            <div className="welcome-popup-body">
              <div className="welcome-illustration">
                <div className="celebration-scene">
                  <img
                    src={WelcomeCuate1}
                    alt="Welcome Illustration"
                    className="welcome-image"
                  />
                </div>
                <div className="confetti">
                  {Array.from({ length: 20 }).map((_, index) => (
                    <div key={index} className={`confetti-piece piece-${index + 1}`}></div>
                  ))}
                </div>
              </div>

              <div className="welcome-text-content">
                <h1 className="welcome-title">
                  {t('WELCOME_ON_BOARD')}
                </h1>
                <p className="welcome-description">
                  {t('THANK_YOU_FOR_TRUST')}
                </p>
              </div>
            </div>

            <div className="welcome-popup-footer">
              <div className="progress-dots">
                <div className="dot active"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>

              <button
                type="button"
                className="welcome-next-btn"
                onClick={handleWelcomeNext}
              >
                {t('NEXT_STEP')}
              </button>
            </div>
          </div>
        </GenericModal>
      </div>

      {Number(userType) === 1 &&
        <CompleteProfileModal
          onStartTour={handleStartTourClick}
          show={show && isProfileStep}
          onClose={handleProfileClose}
          onNext={handleProfileNext}
          initialStep={initialStep}
        />
      }
      {Number(userType) === 2 && (
      <BusinessProfileModal
      onStartTour={handleStartTourClick}
        show={show && isProfileStep}
        onClose={handleProfileClose}
        onNext={handleProfileNext}
        initialStep={initialStep}
      />
      )}
    </>
  );
};

export default WelcomePopupModal;
